import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service';
import { AuthManager } from 'src/app/shared/auth/AuthManager';
import { ApplicationArea } from 'src/app/shared/services/ppv.api';
import moment from 'moment';
import { Editor, Toolbar, toHTML } from 'ngx-editor';


@Component({
  selector: 'vc-goal-details',
  standalone: false,
  templateUrl: './goal-details.component.html',
  styleUrl: './goal-details.component.scss'
})
export class GoalDetailsComponent implements OnInit, OnDestroy {
  goalDetails: any;
  isLoading = false;
  currentUserName = '';
  goalActivities: any[] = [];
  activityGroups: Array<{ key: string; label: string; items: any[]; expanded: boolean }> = [];
  newActivityText :string= '';
  showConfirmDialog = false;
  confirmDialogMessage = '';
  confirmDialogTitle = '';
  pendingAction: 'approve' | 'reject' | 'delete' | null = null;
  showEditTargetDialog = false;
  recommendedTargetInput: number | null = null;
  recommendedTargetComment = 'Target updated';
  isSavingRecommendedTarget = false;
  recommendedFlag = false;
  deleteComment = '';
  approveComment = 'Approved';
  rejectComment = 'Reassigned';
  goalTargetId: any;
  targetReasoning: string = '';
  editor: Editor = new Editor();
  editEditor: Editor = new Editor();
  editingActivity: any = null;
  editText: string = '';
  deleteConfirmActivity: any = null;
  newActivityIsHmVisible = false;
  editIsHmVisible = false;

  isAdmin = false;
  isAdmRole = false;
  isHmRole = false;
  isPublishing = false;
  isEditMode = false;
  isSaving = false;
  isGoalInfoExpanded = false;
  hmToggleValue = false;
  statusOptions: any[] = [];
  executionHealthOptions: any[] = [];
  rmAttentionOptions: any[] = [
    { value: 'ACTION REQUIRED', label: 'Action Required' },
    { value: 'MONITOR', label: 'Monitor' },
    { value: 'NO ACTION NEEDED', label: 'No Action Needed' }
  ];

  editForm = {
    status: '',
    executionHealth: '',
    rmAttention: '',
    name: '',
    category: '',
    startDate: '',
    endDate: '',
    description: ''
  };

  get isDetailTargetDifferent(): boolean {
    const recommended = this.goalDetails?.recommended ?? null;
    const submitted = this.goalDetails?.recommendedTarget ?? null;
    if (recommended === null || submitted === null) {
      return false;
    }
    return Number(recommended) !== Number(submitted);
  }

  get isRecommendedTargetInvalid(): boolean {
    if (this.recommendedTargetInput === null || this.recommendedTargetInput === undefined) {
      return false;
    }
    if (this.recommendedTargetInput < 0) {
      return true;
    }
    const isPercent = (this.goalDetails?.recommendedType ?? '').toString().trim().toUpperCase() === '%';
    if (isPercent && this.recommendedTargetInput > 100) {
      return true;
    }
    return false;
  }
  getTargetPlaceholder(): string {
    const type = (this.goalDetails?.recommendedType ?? '').toString().trim();
    if (type === '%') return 'Enter target (%)';
    if (type === '$') return 'Enter target ($)';
    if (type === '#') return 'Enter target';
    return 'Enter target';
  }
  getTargetHint(): string {
    const type = (this.goalDetails?.recommendedType ?? '').toString().trim();
    if (type === '%') return 'Enter a percent between 0 and 100.';
    return '';
  }
  getTargetWarning(): string {
    const type = (this.goalDetails?.recommendedType ?? '').toString().trim();
    if (type === '%') return 'Please enter a value between 0 and 100%.';
    if (type === '$') return 'Please enter a non-negative dollar value.';
    if (type === '#') return 'Please enter a non-negative whole number.';
    return 'Please enter a non-negative value.';
  }
  get isRecommendedTargetCommentEmpty(): boolean {
    return !this.recommendedTargetComment || this.recommendedTargetComment.trim().length === 0;
  }
  get isTargetDifferentFromRecommended(): boolean {
    const recommended = this.goalDetails?.recommended ?? null;
    const submitted = this.recommendedTargetInput ?? null;

    if (recommended === null || submitted === null) {
      return false;
    }

    return Number(recommended) !== Number(submitted);
  }
  get isCommentRequiredButEmpty(): boolean {
    return this.isTargetDifferentFromRecommended && this.isRecommendedTargetCommentEmpty;
  }
  get canConfirmTargetEdit(): boolean {
    if (this.isRecommendedTargetInvalid) {
      return false;
    }
    if (this.isCommentRequiredButEmpty) {
      return false;
    }
    return true;
  }
  get isRejectCommentEmpty(): boolean {
    return !this.rejectComment || this.rejectComment.trim().length === 0;
  }

  get lastQuarterQtd(): any {
    return this.goalDetails?.lQ_qtd ?? this.goalDetails?.LQ_qtd
      ?? this.goalDetails?.lastQuarterQtd ?? this.goalDetails?.lastQtrQtd ?? this.goalDetails?.lqtd ?? null;
  }

  get lastQuarter(): any {
    return this.goalDetails?.sVperDvmDay_LastQtr ?? this.goalDetails?.SVperDvmDay_LastQtr
      ?? this.goalDetails?.lastQuarter ?? this.goalDetails?.lastQtr ?? null;
  }

  get baseline(): any {
    return this.goalDetails?.baseline ?? this.goalDetails?.baselineValue ?? null;
  }

  get baselineQtd(): any {
    return this.goalDetails?.baselineQtd ?? this.goalDetails?.baseline_qtd ?? null;
  }

  get gapToTarget(): any {
    return this.goalDetails?.gap_to_Target ?? this.goalDetails?.Gap_to_Target
      ?? this.goalDetails?.gapToTarget ?? this.goalDetails?.gapToGoal ?? null;
  }

  get gapToTargetValue(): number | null {
    return this.gapToTargetRaw();
  }

  private gapToTargetRaw(): number | null {
    const raw = this.goalDetails?.gap_to_Target ?? this.goalDetails?.Gap_to_Target
      ?? this.goalDetails?.gapToTarget ?? this.goalDetails?.gapToGoal;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const num = Number(String(raw).replace(/,/g, ''));
    return isNaN(num) ? null : num;
  }

  get isOnTrack(): boolean {
    const value = this.toReachTargetPerWeek;
    const uom = this.goalDetails?.uom ?? this.goalDetails?.UOM ?? this.getGapToTargetUom();

    return this.gapToTargetValue === null || (uom || '').toString().trim().toUpperCase() === 'ON TRACK';
  }

  get toReachTargetPerWeek(): number | null {
    const raw = this.goalDetails?.to_Reach_Target ?? this.goalDetails?.To_Reach_Target;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const num = Number(String(raw).replace(/,/g, ''));
    return isNaN(num) ? null : num;
  }

  private get goalIdKey(): number {
    const id = this.goalDetails?.goalId ?? this.goalDetails?.GoalId
      ?? this.goalDetails?.goal_id ?? this.goalDetails?.goalDefinitionId
      ?? this.goalDetails?.goalDictionaryId ?? null;
    const num = Number(id);
    return isNaN(num) ? -1 : num;
  }

  get toReachGoalText(): string {
    if (this.isOnTrack) {
      return 'On Track';
    }
    const value = this.toReachTargetPerWeek;
    if (value === null) {
      return '—';
    }
    const formatted = this.formatValue(value, this.getGoalFormatType());
    const uom = this.goalDetails?.uom ?? this.goalDetails?.UOM ?? this.getGapToTargetUom();
    if (value === 0 || (uom || '').toString().trim().toUpperCase() === 'ON TRACK') {
      return 'On Track';
    }
    return uom ? `${formatted} ${uom}` : formatted;
  }

  formatValue(value: number | null | undefined, type: any): string {
    if (value === null || value === undefined) {
      return '—';
    }
    const num = Number(value);
    if (isNaN(num)) {
      return String(value);
    }
    const typeStr = (type || '').toString().trim();
    switch (typeStr) {
      case '$': {
        if(num < 0) {
          return '-$' + Math.abs(num).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
        return '$' + num.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });}
      case '%':
        return num + '%';
      case '#':
        return num.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
      default:
        return num.toString();
    }
  }

  getGoalFormatType(): string {
    switch (this.goalIdKey) {
      case 1:
      case 4:
      case 6:
        return '$';
      case 15:
        return '%';
      case 2:
      case 3:
      case 7:
      case 8:
      case 9:
      case 10:
      case 12:
      case 13:
      case 14:
      case 16:
        return '#';
      default:
        return '';
    }
  }

  get goalFileUrl(): string {
    return this.goalDetails?.linkToResource
      ?? this.goalDetails?.LinkToResource
      ?? this.goalDetails?.goalFileUrl
      ?? this.goalDetails?.onePagerLink
      ?? this.goalDetails?.goalFileLink
      ?? this.goalDetails?.fileUrl
      ?? '';
  }

  get goalFileName(): string {
    const fileName = this.getGoalFileName();
    return fileName
      || this.goalDetails?.goalFileName
      || this.goalDetails?.goalFile
      || this.goalDetails?.onePagerName
      || this.goalDetails?.resourceName
      || this.goalDetails?.goalName
      || '';
  }

  getGapToTargetUom(): string {
    switch (this.goalIdKey) {
      case 1:
        return 'of incremental revenue per week';
      case 2:
        return 'incremental service visits per DVM day';
      case 3:
        return 'clients to use Home Delivery';
      case 4:
        return 'Incremental Revenue per Week';
      case 5:
        return '';
      case 6:
        return 'Material Decrease per Week';
      case 7:
        return 'Decrease in support staff hours per week';
      case 8:
        return 'Incremental number of Forward Book Appointments per week';
      case 9:
        return 'Incremental checkout links sent';
      case 10:
        return 'Incremental new patients per week';
      case 11:
        return 'Incremental preventative patients per week';
      case 12:
        return 'Total incremental client contacts needed';
      case 13:
        return 'Incremental insurance transactions per week';
      case 14:
        return 'Incremental wellness labs per week';
      case 15:
        return 'Additional client show rate as a percentage of remaining appointments in the quarter.';
      case 16:
        return 'Total additional client call answers required';
      default:
        return '';
    }
  }

  getGoalFileName(): string {
    switch (this.goalIdKey) {
      case 1:
        return 'In-House Revenue One Pager';
      case 2:
        return 'Service Visits per DVM Day One Pager';
      case 3:
        return 'Home Delivery Client Penetration One Pager';
      case 4:
        return 'DVM Caseload One Pager';
      case 5:
        return 'Net Promoter Score One Pager';
      case 6:
        return 'Material Ordering One Pager';
      case 7:
        return 'Support Staff Payroll One Pager';
      case 8:
        return 'Continuous Care One Pager';
      case 9:
        return 'Checkout Links One Pager';
      case 10:
        return '';
      case 11:
        return 'Preventatives One Pager';
      case 12:
        return '';
      case 13:
        return 'Insurance & Financing One Pager';
      case 14:
        return '';
      case 15:
        return '';
      case 16:
        return 'GROW Session Call Answer Rates';
      default:
        return '';
    }
  }

  formatMetric(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    const num = Number(String(value).replace(/,/g, ''));
    if (isNaN(num)) {
      return String(value);
    } else{
      return '—';
    }
  }

  constructor(
    private dialog: MatDialog,
    public leaderService: LeaderService,
    @Inject(MAT_DIALOG_DATA) private dialogdata: any,
    private authMgr: AuthManager,
    private dialogRef: MatDialogRef<GoalDetailsComponent>
  ) { }

  ngOnInit(): void {

    this.goalTargetId = this.dialogdata;
    this.loadCurrentUser();
    this.checkAdminRole();
    this.checkAdmRole();
    this.getGoalDetails();
    this.loadGoalActivities();
    this.loadLookups();
  }

  loadLookups() {
    this.leaderService.geGoalStatusFilterData('GoalStatus').subscribe((res: any) => {
      const statuses = res?.data?.GoalStatus || [];
      this.statusOptions = statuses.map((s: any) => {
        const val = typeof s === 'string' ? s : (s?.value ?? s?.name ?? '');
        return { value: val, label: this.getStatusLabel(val) };
      });
    });

    this.leaderService.geGoalStatusFilterData('ExecutionHealth').subscribe((res: any) => {
      const healths = res?.data?.ExecutionHealth || [];
      this.executionHealthOptions = healths.map((h: any) => {
        const val = typeof h === 'string' ? h : (h?.value ?? h?.name ?? '');
        return { value: val, label: val };
      });
    });
  }

  enterEditMode() {
    this.editForm = {
      status: this.goalDetails?.status || '',
      executionHealth: this.goalDetails?.executionHealth || '',
      rmAttention: this.goalDetails?.rmActionRequired || this.goalDetails?.rMActionRequired || '',
      name: this.goalDetails?.goalName || '',
      category: this.goalDetails?.category || '',
      startDate: this.goalDetails?.startDate ? moment(this.goalDetails.startDate).format('YYYY-MM-DD') : '',
      endDate: this.goalDetails?.endDate ? moment(this.goalDetails.endDate).format('YYYY-MM-DD') : '',
      description: this.goalDetails?.description || ''
    };
    this.isEditMode = true;
  }

  cancelEdit() {
    this.isEditMode = false;
  }

  toggleGoalInfo() {
    this.isGoalInfoExpanded = !this.isGoalInfoExpanded;
  }

  hasGoalInfoContent(): boolean {
    return !!(this.goalDetails?.description || this.goalDetails?.explanation || this.goalDetails?.calcDisplay);
  }

  saveGoalChanges() {
    this.isSaving = true;

    const payload = {
      goalTargetId: this.goalTargetId,
      status: this.editForm.status,
      currentStatusId: this.goalDetails?.currentStatusId ?? this.goalDetails?.statusId ?? this.goalDetails?.goalStatusId ?? 0
    };

    this.leaderService.updateGoalTargetStatus(payload).subscribe({
      next: (res: any) => {
        if (res?.resultDetails?.[0]?.status === 'SUCCESS' || res?.status === 'SUCCESS') {
          this.handleSaveSuccess();
        } else {
          this.handleSaveError(res?.resultDetails?.[0]?.message || 'Failed to update goal status');
        }
      },
      error: () => {
        this.handleSaveError('An error occurred while updating the goal status');
      }
    });
  }

  private handleSaveSuccess() {
    this.leaderService.showNotification('Goal updated successfully', false);
    this.getGoalDetails();
    this.isEditMode = false;
    this.isSaving = false;
    this.leaderService.triggerOpsEventUpdate(true);
  }

  private handleSaveError(message: string) {
    this.leaderService.showNotification(message, true);
    this.isSaving = false;
  }

  ngOnDestroy(): void {
    this.editor.destroy();
    this.editEditor.destroy();
  }

  loadCurrentUser() {
    this.authMgr.getCurrentUser().subscribe((user: any) => {
      this.currentUserName = user?.displayName || user?.name || '';
    });
  }

  checkAdminRole() {
    this.leaderService.hasAccessToManageUsersLtk().subscribe((res: any) => {
      this.isAdmin = res === true || res?.data === true;
    });
  }

  checkAdmRole() {
    this.authMgr.getCurrentUser().subscribe((user: any) => {
      const appRole = user?.roles?.find((r: any) => r?.application === ApplicationArea.LTK && r?.roleName !== 'LeadershipToolKit');
      this.isAdmRole = appRole?.roleName === 'ADM';
      this.isHmRole = appRole?.roleName === 'HM';
    });
  }

  publishGoal() {
    if (this.isPublishing) return;
    this.isPublishing = true;
    const requestData = {
      goalTargetId: this.goalTargetId
    };
    this.leaderService.updateGoalTargetIsHm(requestData)
      .subscribe({
        next: (res: any) => {
          this.isPublishing = false;
          if (res?.resultDetails?.[0]?.status === 'SUCCESS' || res?.status === 'SUCCESS') {
            this.leaderService.showNotification('Goal published successfully.', false);
            this.getGoalDetails();
            this.leaderService.triggerOpsEventUpdate(true);
          } else {
            this.leaderService.showNotification(res?.resultDetails?.[0]?.message || 'Failed to publish goal', true);
          }
        },
        error: (error) => {
          this.isPublishing = false;
          this.leaderService.showNotification('An error occurred while publishing the goal.', true);
          console.error('Error publishing goal:', error);
        }
      });
  }

  onHmToggleChange(newIsHm: boolean) {
    if (this.isPublishing) return;
    this.isPublishing = true;
    const requestData = {
      goalTargetId: this.goalTargetId,
      isHM: newIsHm
    };
    this.leaderService.updateGoalTargetIsHm(requestData)
      .subscribe({
        next: (res: any) => {
          this.isPublishing = false;
          if (res?.resultDetails?.[0]?.status === 'SUCCESS' || res?.status === 'SUCCESS') {
            this.goalDetails.isHM = newIsHm;
            this.leaderService.showNotification(
              newIsHm ? 'Goal published to HM dashboard' : 'Goal hidden from HM dashboard',
              false
            );
            this.leaderService.triggerOpsEventUpdate(true);
          } else {
            this.hmToggleValue = !newIsHm;
            this.leaderService.showNotification(res?.resultDetails?.[0]?.message || 'Failed to publish goal', true);
          }
        },
        error: (error) => {
          this.isPublishing = false;
          this.hmToggleValue = !newIsHm;
          this.leaderService.showNotification('An error occurred while updating visibility.', true);
          console.error('Error toggling HM visibility:', error);
        }
      });
  }

  getCreatedByName(): string {
    return this.goalDetails?.createdByName
      || this.goalDetails?.goalTarget?.createdByName
      || this.goalDetails?.createdBy
      || this.goalDetails?.submittedBy
      || '—';
  }

  canDeleteGoal(): boolean {

    if (this.goalDetails?.canEditAllFields) {
      return this.goalDetails?.canEditAllFields;
    }

    const status = (this.goalDetails?.status || '').toString().trim().toUpperCase();

    if (status === 'ASSIGNED' || status === 'DELETED' || status === 'LIVE') {
      return false;
    }

    if (this.goalDetails?.isCanUserActOn) {
      return true;
    }

    const createdBy = this.getCreatedByName().toString().trim().toLowerCase();
    const currentUser = (this.currentUserName || '').toString().trim().toLowerCase();

    return !!createdBy && !!currentUser && createdBy === currentUser;
  }

  getGoalDetails() {
    this.isLoading = true;
    this.leaderService.getGoalDetailsById(this.goalTargetId)
      .subscribe((res: any) => {
        if (!res || !res.data) {
          this.leaderService.showNotification('Goal details not found.', true);
          this.isLoading = false;
          return;
        }
        this.goalDetails = res.data || [];
        this.hmToggleValue = !!this.goalDetails?.isHM;
        this.isLoading = false;
      },
        (error) => {
          this.leaderService.showNotification('Error loading goal details.', true);
          console.error('Error loading goal details:', error);
          this.isLoading = false;
        });
  }

  loadGoalActivities() {
    this.leaderService.getApprovalHistory(this.goalTargetId).subscribe({
      next: (res: any) => {
        const historyItems = (res?.data || []).map((item: any) => ({
          ...item,
          sortDate: item.actionDate
        }));

        historyItems.sort((a: any, b: any) => {
          const dateA = new Date(a.sortDate || 0).getTime();
          const dateB = new Date(b.sortDate || 0).getTime();
          return dateB - dateA;
        });

        this.goalActivities = historyItems;
        this.buildActivityGroups();
        this.extractTargetReasoning();
      },
      error: (error) => {
        this.leaderService.showNotification('Error loading activities.', true);
        console.error('Error loading activities:', error);
        this.goalActivities = [];
        this.activityGroups = [];
      }
    });
  }

  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  extractTargetReasoning() {
    const reasoningPrefix = 'Target Reasoning: ';
    const reasoningActivity = (this.goalActivities || []).find((a: any) =>
      a?.comments?.startsWith(reasoningPrefix)
    );
    if (reasoningActivity) {
      this.targetReasoning = reasoningActivity.comments.substring(reasoningPrefix.length);
    } else {
      this.targetReasoning = '';
    }
  }

  postActivity() {
    const textContent = this.editor.view?.state?.doc?.textContent?.trim();
    if (!textContent) {
      return;
    }

    let htmlContent: string;
    try {
      htmlContent = toHTML(this.editor.view.state.doc.toJSON() as Record<string, unknown>, this.editor.schema);
    } catch {
      htmlContent = typeof this.newActivityText === 'string' ? this.newActivityText : textContent;
    }

    const activityData = {
      goaltargetid: this.dialogdata,
      comments: htmlContent,
      isHMVisible: this.isHmRole || this.newActivityIsHmVisible,
    };

    this.leaderService.createGoalComment(activityData)
      .subscribe((res: any) => {
        if (res?.resultDetails?.[0]?.status === 'SUCCESS') {
          this.loadGoalActivities();
          this.leaderService.showNotification('Comment posted successfully.', false);
          this.newActivityText = '';
          this.newActivityIsHmVisible = false;
          this.leaderService.triggerOpsEventUpdate(true);
        }
      },
        (error) => {
          this.leaderService.showNotification('Error posting activity.', true);
          console.error('Error posting activity:', error);
        });
  }

  isCommentActivity(activity: any): boolean {
    return (activity?.actionType || '').toString().toUpperCase() === 'COMMENT';
  }

  isOwnComment(activity: any): boolean {
    const name = activity?.displayName || '';
    return name.toLowerCase() === this.currentUserName.toLowerCase();
  }

  isActivityHmVisible(activity: any): boolean {
    return activity?.isHMVisible === true;
  }

  startEditActivity(activity: any) {
    this.editingActivity = activity;
    this.editText = activity.comments || '';
    this.editIsHmVisible = this.isActivityHmVisible(activity);
  }

  cancelEditActivity() {
    this.editingActivity = null;
    this.editText = '';
    this.editIsHmVisible = false;
  }

  saveEditActivity() {
    if (!this.editText.trim()) return;
    const data = {
      goalTargetId: this.goalTargetId,
      commentId: this.editingActivity.approvalId,
      comments: this.editText,
      isHMVisible: this.isHmRole || this.editIsHmVisible
    };
    this.leaderService.updateGoalComment(data).subscribe({
      next: (res: any) => {
        this.leaderService.showNotification('Comment updated successfully!', false);
        this.editingActivity = null;
        this.editText = '';
        this.editIsHmVisible = false;
        this.loadGoalActivities();
      },
      error: () => {
        this.leaderService.showNotification('Failed to update comment', true);
      }
    });
  }

  confirmDeleteActivity(activity: any) {
    this.deleteConfirmActivity = activity;
  }

  cancelDeleteActivity() {
    this.deleteConfirmActivity = null;
  }

  deleteActivityComment() {
    if (!this.deleteConfirmActivity) return;
    const data = {
      goalTargetId: this.goalTargetId,
      commentId: this.deleteConfirmActivity.approvalId
    };
    this.leaderService.deleteGoalComment(data).subscribe({
      next: (res: any) => {
        this.leaderService.showNotification('Comment deleted successfully!', false);
        this.deleteConfirmActivity = null;
        this.loadGoalActivities();
      },
      error: () => {
        this.leaderService.showNotification('Failed to delete comment', true);
      }
    });
  }

  updateGoalStatus() {
  }

  onClose() {
    this.dialogRef.close();
  }

  getStatusClass(status: string): string {
    if (!status) return 'pending';

    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case 'completed':
      case 'complete':
      case 'approved':
      case 'live':
        return 'completed';
      case 'pending':
      case 'pending_hm_review':
      case 'pending_rm_review':
      case 'pending_vp_review':
      case 'assigned':
      case 'in_progress':
        return 'pending';
      case 'rejected':
      case 'cancelled':
      case 'deleted':
        return 'rejected';
      default:
        return 'pending';
    }
  }

  getStatusLabel(status: string): string {
    const normalized = (status || '').toString().trim().toUpperCase();
    switch (normalized) {
      case 'PENDING_VP_REVIEW':
        return 'VP/DO Review';
      case 'PENDING_RM_REVIEW':
        return 'RM Review';
      default:
        return status || '';
    }
  }

  formatActivityDate(actionDate: any): string {
    if (!actionDate) {
      return '—';
    }
    try {
      const date = moment.utc(actionDate).local();
      return date.format('MMM DD, YYYY hh:mm a');
    } catch (error) {
      console.error('Error formatting date:', error);
      return actionDate;
    }
  }

  getActivityIcon(activity: any): string {
    const actionType = (activity?.actionType || '').toString().toLowerCase();
    const comments = (activity?.comments || '').toString().toLowerCase();
    const fromStatus = (activity?.fromStatus || '').toString().toLowerCase();
    const toStatus = (activity?.toStatus || '').toString().toLowerCase();

    if (actionType.includes('approve')) return 'check_circle';
    if (actionType.includes('reject')) return 'reply';
    if (actionType.includes('delete')) return 'delete';
    if (actionType.includes('comment') || comments) return 'comment';
    if (actionType.includes('task')) return 'check_box';
    if (actionType.includes('goal')) return 'flag';
    if (fromStatus || toStatus) return 'sync_alt';
    return 'event';
  }

  getActivityIconClass(activity: any): string {
    const actionType = (activity?.actionType || '').toString().toLowerCase();
    if (actionType.includes('approve')) return 'icon-approved';
    if (actionType.includes('reject')) return 'icon-rejected';
    if (actionType.includes('delete')) return 'icon-deleted';
    if (actionType.includes('task')) return 'icon-task';
    if (actionType.includes('goal')) return 'icon-goal';
    return 'icon-default';
  }

  buildActivityGroups(): void {
    const groups = new Map<string, { key: string; label: string; items: any[]; expanded: boolean }>();
    (this.goalActivities || []).forEach((activity) => {
      const label = (activity?.actionType || 'Other').toString().trim() || 'Other';
      const key = label.toLowerCase();
      if (!groups.has(key)) {
        groups.set(key, { key, label, items: [], expanded: false });
      }
      groups.get(key)!.items.push(activity);
    });

    this.activityGroups = Array.from(groups.values());
  }

  toggleActivityGroup(group: { expanded: boolean }): void {
    group.expanded = !group.expanded;
  }

  openConfirmDialog(action: 'approve' | 'reject' | 'delete') {
    this.pendingAction = action;

    switch (action) {
      case 'approve':
        this.confirmDialogTitle = 'Approve Goal';
        this.confirmDialogMessage = 'Are you sure you want to approve this goal?';
        break;
      case 'reject':
        this.confirmDialogTitle = 'Re Assign Goal';
        this.confirmDialogMessage = 'Are you sure you want to reassign this goal?';
        break;
      case 'delete':
        this.confirmDialogTitle = 'Delete Goal';
        this.confirmDialogMessage = 'Are you sure you want to delete this goal? This action cannot be undone.';
        break;
    }

    this.showConfirmDialog = true;
  }

  confirmAction() {
    if (!this.pendingAction) return;

    switch (this.pendingAction) {
      case 'approve':
        this.approveGoal();
        break;
      case 'reject':
        this.rejectGoal();
        break;
      case 'delete':
        this.deleteGoal();
        break;
    }

    this.closeConfirmDialog();
  }

  closeConfirmDialog() {
    this.showConfirmDialog = false;
    this.pendingAction = null;
    this.confirmDialogMessage = '';
    this.confirmDialogTitle = '';
    this.deleteComment = '';
    this.approveComment = '';
    this.rejectComment = '';
  }

  approveGoal() {
    const requestData = {
      goalTargetId: this.goalTargetId,
      isAction: true,
      comment: this.approveComment || 'Approved',
    };
    this.leaderService.processGoalApproval(requestData)
      .subscribe((res: any) => {
        if (res?.resultDetails[0].status === 'SUCCESS') {
          this.leaderService.showNotification('Goal approved successfully.', false);
          this.dialogRef.close('success'); // Close dialog and pass result
        }
      },
        (error) => {
          console.error('Error approving goal:', error);
        });
  }

  rejectGoal() {
    const requestData = {
      goalTargetId: this.goalTargetId,
      isAction: false,
      comment: this.rejectComment || 'Goal rejected',
    };
    this.leaderService.processGoalApproval(requestData)
      .subscribe((res: any) => {
        if (res?.resultDetails[0].status === 'SUCCESS') {
          this.leaderService.showNotification('Goal reassigned successfully.', false);
          this.dialogRef.close('success'); // Close dialog and pass result

        }
      },
        (error) => {
          console.error('Error rejecting goal:', error);
        });
  }

  deleteGoal() {
    const requestData = {
      GoalTargetId: this.goalTargetId,
      Comments: this.deleteComment
    };
    this.leaderService.deleteGoalTarget(requestData)
      .subscribe((res: any) => {
        if (res?.resultDetails[0].status === 'SUCCESS') {
          this.leaderService.showNotification('Goal deleted successfully.', false);
          this.dialogRef.close('success');
        }
      },
        (error) => {
          console.error('Error deleting goal:', error);
        });
  }

  openEditTargetDialog() {
    this.recommendedTargetInput = this.goalDetails?.recommendedTarget ?? null;
    this.recommendedTargetComment = '';
    this.recommendedFlag = !!this.goalDetails?.recommendedFlag;
    this.showEditTargetDialog = true;
  }

  closeEditTargetDialog() {
    if (this.isSavingRecommendedTarget) {
      return;
    }
    this.showEditTargetDialog = false;
    this.recommendedTargetInput = null;
    this.recommendedTargetComment = '';
    this.recommendedFlag = false;
  }

  confirmEditTarget() {
    if (this.recommendedTargetInput === null || this.recommendedTargetInput === undefined) {
      return;
    }

    // Validate that comment is provided when target differs from recommended
    if (this.isCommentRequiredButEmpty) {
      this.leaderService.showNotification('Comment is required when submitted target differs from recommended target.', true);
      return;
    }

    const payload = {
      GoalTargetId: this.goalDetails?.goalTargetId ?? this.dialogdata,
      RecommendedTarget: this.recommendedTargetInput,
      Comments: this.recommendedTargetComment || '',
      RecommendedFlag: this.recommendedFlag,
      recommendationReason: this.isTargetDifferentFromRecommended && this.recommendedTargetComment?.trim()
        ? this.recommendedTargetComment.trim()
        : null
    };
    this.isSavingRecommendedTarget = true;
    this.leaderService.updateRecommendedTarget(payload)
      .subscribe((res: any) => {
        if (res?.resultDetails[0].status === 'SUCCESS') {
          this.goalDetails = {
            ...this.goalDetails,
            recommendedTarget: this.recommendedTargetInput
          };
          this.closeEditTargetDialog();
          this.leaderService.showNotification('Recommended target updated successfully.', false);
          this.showEditTargetDialog = false;
          this.isSavingRecommendedTarget = false;
          this.dialogRef.close('success'); // Close dialog and pass result
        }

      },
        (error) => {
          console.error('Error updating recommended target:', error);
          this.isSavingRecommendedTarget = false;
        });
  }
}
